/**
 * travaux-tertiaire.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration centralisée des pages "stubs" /tertiaire/travaux/[slug].
 *
 * Décrit les principales opérations mobilisables sur un patrimoine
 * tertiaire pour réduire durablement vos consommations.
 */

import type { ParcoursStub } from './parcours-particuliers';

export const TRAVAUX_TERTIAIRE: ParcoursStub[] = [
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'pac-tertiaire',
    iconName: 'snowflake',
    eyebrow: 'Pompe à chaleur tertiaire, fiche CEE (BAT-TH-104)',
    title: 'Pompe à chaleur tertiaire',
    shortLabel: 'PAC tertiaire',
    lead: "Une pompe à chaleur air/eau ou eau/eau remplace votre chauffage au gaz, au fioul ou électrique. Elle peut bénéficier d'un bonus lorsqu'elle remplace une chaudière à combustible fossile.",
    badge: 'BAT-TH-104',
    badgeLabel: "Fiche d'opération standardisée CEE secteur BAT-TH",
    keyPoints: [
      {
        iconName: 'snowflake',
        label: 'Réversible chaud / froid',
        description: "Une pompe à chaleur réversible couvre le chauffage en hiver et le rafraîchissement en été, atout majeur pour vos immeubles.",
      },
      {
        iconName: 'flame',
        label: 'Bonus chauffage',
        description: "Si la pompe à chaleur remplace une chaudière fioul ou gaz, l'aide est majorée.",
      },
      {
        iconName: 'gauge',
        label: 'Performance certifiée',
        description: "Le rendement saisonnier (SCOP) est mesuré selon une norme reconnue. Un seuil minimum est requis.",
      },
      {
        iconName: 'shield-check',
        label: 'Installateur qualifié',
        description: "Installation par un professionnel disposant des qualifications adaptées. Conservation des justificatifs recommandée.",
      },
    ],
    steps: [
      {
        title: 'Étude de faisabilité',
        description: "Bilan de votre chauffage actuel, dimensionnement de la nouvelle pompe à chaleur, vérification des contraintes d'implantation (toiture, local technique, bruit).",
      },
      {
        title: "Calage du dossier d'aide",
        description: "Identification de l'opération applicable, calcul du volume d'économies, vérification de l'éligibilité au bonus.",
      },
      {
        title: 'Engagement et signature des devis',
        description: "L'aide doit être demandée avant de signer le devis. Pièces à conserver : devis, fiche technique de la pompe à chaleur.",
      },
      {
        title: 'Réalisation et réception',
        description: "Pose, mise en service, équilibrage hydraulique, raccordement au pilotage centralisé si existant.",
      },
      {
        title: "Dépôt du dossier et versement",
        description: "Constitution du dossier, dépôt, instruction et versement de l'aide.",
      },
    ],
    faq: [
      {
        question: "Quels types de pompes à chaleur sont éligibles ?",
        answer:
          "Les pompes à chaleur air/eau ou eau/eau installées sur un bâtiment à usage tertiaire. Les modèles air/air relèvent d'une autre catégorie. Le rendement requis et les conditions sont à vérifier au moment de la signature du devis.",
      },
      {
        question: "La pompe à chaleur contribue-t-elle à mes objectifs de réduction ?",
        answer:
          "Oui, dans la quasi-totalité des cas. Remplacer une chaudière fioul ou gaz par une pompe à chaleur réduit fortement la consommation d'énergie finale du bâtiment. Le gain doit être documenté et déclaré annuellement.",
      },
      {
        question: "Faut-il un diagnostic préalable ?",
        answer:
          "Le diagnostic n'est pas exigé pour l'aide elle-même. Il est néanmoins recommandé pour caler votre année de référence et prioriser vos investissements (pompe à chaleur vs isolation vs autres).",
      },
    ],
    ctaLabel: 'Estimer la prime CEE pour une PAC tertiaire (BAT-TH-104)',
    ctaHref: '/tertiaire/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (BAT-TH-104)', href: '/cee/fiches?ref=BAT-TH-104' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'gtb',
    iconName: 'cpu',
    eyebrow: 'Gestion technique du bâtiment, fiche CEE (BAT-TH-116)',
    title: 'Gestion Technique du Bâtiment (GTB)',
    shortLabel: 'GTB',
    lead: "Un système de pilotage qui coordonne vos équipements — chauffage, ventilation, climatisation, éclairage. Socle incontournable de votre plan de réduction des consommations.",
    badge: 'BAT-TH-116',
    badgeLabel: "Fiche d'opération standardisée CEE secteur BAT-TH",
    keyPoints: [
      {
        iconName: 'cpu',
        label: 'Niveaux de performance',
        description: "Les systèmes de pilotage sont classés en 4 niveaux (A « haute performance », B « avancé », C « standard », D « non énergétique »). L'aide cible les niveaux A et B.",
      },
      {
        iconName: 'shield-check',
        label: 'Obligation pour les gros bâtiments',
        description: "Les bâtiments de plus de 290 kW de puissance doivent être équipés d'un pilotage de niveau B minimum.",
      },
      {
        iconName: 'gauge',
        label: 'Levier prioritaire',
        description: "Le pilotage intelligent est l'un des leviers les plus rentables pour réduire vos consommations. Il agit sur tous les postes et améliore le pilotage au quotidien.",
      },
      {
        iconName: 'file-text',
        label: 'Mesure et vérification',
        description: "L'aide exige de mesurer et vérifier les économies réelles pour attester les gains.",
      },
    ],
    steps: [
      {
        title: 'Audit de votre pilotage actuel',
        description: "Inventaire des équipements pilotés, classement du système actuel, identification de l'écart vers le niveau cible.",
      },
      {
        title: 'Définition de la cible',
        description: "Choix du niveau de performance cible, périmètre fonctionnel (chauffage seul ou multi-postes), architecture technique.",
      },
      {
        title: "Calage du dossier d'aide",
        description: "Identification de l'opération applicable, calcul du volume d'économies selon la surface et le niveau cible.",
      },
      {
        title: 'Engagement et mise en œuvre',
        description: "L'aide doit être demandée avant de signer le devis. Déploiement du pilotage (matériel, logiciel, intégration), tests, formation de vos équipes.",
      },
      {
        title: 'Réception et dépôt du dossier',
        description: "Procès-verbal de réception, dépôt du dossier. Suivi des performances sur 12 mois après mise en service.",
      },
    ],
    faq: [
      {
        question: "Quelle différence entre le niveau A et B ?",
        answer:
          "Le niveau B « avancé » couvre le pilotage des principaux équipements avec des fonctions d'optimisation standard. Le niveau A « haute performance » ajoute des fonctions avancées : optimisation prédictive, prise en compte de la météo, pilotage fin selon l'occupation, supervision énergétique complète.",
      },
      {
        question: "Quelle articulation entre les obligations ?",
        answer:
          "Certains bâtiments doivent être équipés d'un pilotage de niveau B minimum. Par ailleurs, vous devez réduire vos consommations dans le temps. Concrètement, le pilotage intelligent répond à la fois à l'obligation d'équipement et constitue un levier majeur pour atteindre vos objectifs de réduction. Les deux démarches se renforcent.",
      },
      {
        question: "Combien d'économies attendre ?",
        answer:
          "Les économies dépendent de votre point de départ (niveau C ou D), du périmètre fonctionnel et de la qualité d'exploitation. Les gains réels sont validés par la démarche de mesure et vérification. Un pilotage bien dimensionné et bien exploité reste souvent le meilleur rapport investissement/économies.",
      },
    ],
    ctaLabel: 'Estimer la prime CEE pour une GTB (BAT-TH-116)',
    ctaHref: '/tertiaire/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (BAT-TH-116)', href: '/cee/fiches?ref=BAT-TH-116' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'ecs-solaire',
    iconName: 'sun',
    eyebrow: 'ECS solaire collective, fiche CEE (BAT-TH-152)',
    title: 'ECS solaire collective tertiaire',
    shortLabel: 'ECS solaire',
    lead: "Des capteurs solaires thermiques pour couvrir une part importante des besoins d'eau chaude de vos bâtiments à fort usage : hôtellerie, restauration collective, EHPAD, équipements sportifs.",
    badge: 'BAT-TH-152',
    badgeLabel: "Fiche d'opération standardisée CEE secteur BAT-TH",
    keyPoints: [
      {
        iconName: 'sun',
        label: 'Usages à forte demande',
        description: "Hôtellerie, restauration collective, EHPAD, internats, équipements sportifs, blanchisseries : plus vous consommez d'eau chaude, plus le solaire est rentable.",
      },
      {
        iconName: 'shield-check',
        label: 'Installateur spécialisé',
        description: "L'installation doit être réalisée par un professionnel qualifié en solaire collectif.",
      },
      {
        iconName: 'gauge',
        label: 'Productivité minimale',
        description: "L'aide exige un rendement minimum des capteurs solaires, vérifié selon les normes reconnues.",
      },
      {
        iconName: 'flame',
        label: 'Cumul avec autres aides',
        description: "L'eau chaude solaire collective est éligible à des aides complémentaires pour les bâtiments existants.",
      },
    ],
    steps: [
      {
        title: 'Audit de vos besoins',
        description: "Estimation de vos besoins quotidiens, profil journalier et saisonnier, état de votre production actuelle, contraintes d'implantation des capteurs (toiture, orientation, ombrage).",
      },
      {
        title: 'Dimensionnement technique',
        description: "Calcul de la surface de capteurs et du volume de stockage. Choix de la configuration et de l'appoint (électrique, gaz, pompe à chaleur).",
      },
      {
        title: "Calage du dossier d'aide",
        description: "Identification de l'opération applicable, calcul du volume d'économies.",
      },
      {
        title: 'Engagement et réalisation',
        description: "L'aide doit être demandée avant de signer le devis. Pose des capteurs, du ballon, du système de régulation et de comptage.",
      },
      {
        title: 'Réception et dépôt',
        description: "Procès-verbal de réception, mise en service avec relevé de production, dépôt du dossier. Suivi de la productivité sur 12 mois.",
      },
    ],
    faq: [
      {
        question: "L'eau chaude solaire est-elle pertinente pour un immeuble de bureaux ?",
        answer:
          "Pour un immeuble de bureaux standard (faible consommation d'eau chaude), c'est rarement le levier le plus rentable. L'eau chaude solaire prend tout son sens sur les usages à forte demande : hôtellerie, restauration collective, vestiaires sportifs, EHPAD, blanchisseries.",
      },
      {
        question: "Comment cumuler avec d'autres aides ?",
        answer:
          "Plusieurs dispositifs peuvent se cumuler sur une même installation, dans la limite des plafonds autorisés. Les grosses installations peuvent bénéficier d'aides complémentaires ; les petites sont financées principalement par l'aide travaux.",
      },
      {
        question: "Quelle contribution à mes objectifs de réduction ?",
        answer:
          "L'eau chaude solaire remplace une énergie fossile ou électrique par une énergie gratuite et renouvelable. Sur les usages à forte demande, le gain peut représenter une part notable des consommations d'eau chaude.",
      },
    ],
    ctaLabel: "Estimer la prime CEE pour l'ECS solaire (BAT-TH-152)",
    ctaHref: '/tertiaire/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (BAT-TH-152)', href: '/cee/fiches?ref=BAT-TH-152' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'isolation-toiture',
    iconName: 'home',
    eyebrow: 'Isolation de toiture-terrasse, fiche CEE (BAT-EN-101)',
    title: 'Isolation de toiture-terrasse',
    shortLabel: 'Isolation toiture-terrasse',
    lead: "Isoler votre toiture-terrasse réduit fortement les pertes de chaleur des immeubles à toit plat — bureaux, commerces, locaux d'activité. L'une des opérations les plus rentables pour diminuer durablement vos consommations, surtout sur les bâtiments construits avant 2005.",
    badge: 'BAT-EN-101',
    badgeLabel: "Fiche d'opération standardisée CEE secteur BAT-EN",
    keyPoints: [
      {
        iconName: 'home',
        label: 'Toitures plates',
        description: "L'opération vise les toitures-terrasses, c'est-à-dire les toitures plates (pente ≤ 5 %) étanchéifiées.",
      },
      {
        iconName: 'shield-check',
        label: 'Performance minimale',
        description: "L'aide exige un niveau minimum d'isolation. Plus vous isolez, meilleur est le gain sur la durée.",
      },
      {
        iconName: 'sparkles',
        label: 'Levier-clé pour le bâti ancien',
        description: "Sur un patrimoine construit avant 1990, l'isolation de toiture offre souvent l'un des meilleurs ratios investissement/économies.",
      },
      {
        iconName: 'file-text',
        label: 'Justificatifs techniques',
        description: "Certificats et fiches techniques de l'isolant. Conservation recommandée pour les contrôles.",
      },
    ],
    steps: [
      {
        title: 'Diagnostic de la toiture',
        description: "État de l'étanchéité, présence d'isolant existant, ponts thermiques, charges admissibles, contraintes d'accès.",
      },
      {
        title: 'Choix de la solution',
        description: "Sélection de la technique (isolation inversée, sandwich, panneaux isolants, laine minérale), compatibilité avec la couverture existante.",
      },
      {
        title: "Calage du dossier d'aide",
        description: "Identification de l'opération applicable, calcul du volume d'économies selon la surface.",
      },
      {
        title: 'Engagement et réalisation',
        description: "L'aide doit être demandée avant de signer le devis. Réalisation des travaux, gestion des accès et de la coactivité.",
      },
      {
        title: 'Réception et dépôt',
        description: "Procès-verbal de réception, conservation des justificatifs. Dépôt du dossier.",
      },
    ],
    faq: [
      {
        question: "Quel niveau d'isolation viser ?",
        answer:
          "L'aide impose un niveau minimum. Au-delà, il peut être pertinent de viser un niveau plus élevé pour maximiser le gain énergétique sur la durée et améliorer votre trajectoire de réduction des consommations.",
      },
      {
        question: "L'isolation de toiture contribue-t-elle à mes objectifs ?",
        answer:
          "Oui, directement. La réduction des déperditions diminue les besoins de chauffage et de climatisation, donc votre consommation. Sur un immeuble ancien, l'isolation de toiture fait partie des opérations les plus structurantes.",
      },
      {
        question: "Peut-on combiner avec une réfection d'étanchéité ?",
        answer:
          "Oui, c'est même le cas le plus fréquent. La réfection d'étanchéité (non éligible seule) devient l'occasion d'intégrer ou de renforcer l'isolation, ce qui rend l'opération éligible. Le rapport investissement/économies devient très favorable.",
      },
    ],
    ctaLabel: "Estimer la prime CEE pour l'isolation toiture (BAT-EN-101)",
    ctaHref: '/tertiaire/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (BAT-EN-101)', href: '/cee/fiches?ref=BAT-EN-101' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'relamping-led',
    iconName: 'sparkles',
    eyebrow: 'Relamping LED, fiche CEE (BAT-EQ-127)',
    title: 'Relamping LED haute efficacité',
    shortLabel: 'Relamping LED',
    lead: "Le relamping LED remplace vos anciens éclairages — tubes fluorescents, lampes à vapeur de mercure, halogènes — par des luminaires LED haute efficacité. L'opération la plus simple et rapide pour faire baisser vos consommations.",
    badge: 'BAT-EQ-127',
    badgeLabel: "Fiche d'opération standardisée CEE secteur BAT-EQ",
    keyPoints: [
      {
        iconName: 'sparkles',
        label: 'Efficacité lumineuse minimale',
        description: "L'aide impose un niveau de performance minimum des luminaires LED (rendement en lumens par watt) et une durée de vie garantie.",
      },
      {
        iconName: 'gauge',
        label: 'Variateurs et détection',
        description: "L'éligibilité peut être conditionnée à la présence de variateurs ou détecteurs de présence selon l'usage. Cela renforce les économies.",
      },
      {
        iconName: 'shield-check',
        label: 'Matériel certifié',
        description: "Le luminaire doit disposer du marquage CE et d'une fiche technique attestant ses performances.",
      },
      {
        iconName: 'flame',
        label: 'Gain rapide',
        description: "Le relamping LED se déploie vite (chantier court, sans gros œuvre) et apporte un gain mesurable dès la mise en service.",
      },
    ],
    steps: [
      {
        title: "Inventaire de l'éclairage",
        description: "Recensement par bâtiment et par usage (bureaux, circulations, sanitaires, parkings). Type de source, puissance installée, temps de fonctionnement annuel.",
      },
      {
        title: 'Choix des luminaires LED',
        description: "Sélection des luminaires selon l'usage. Validation des fiches techniques et certificats.",
      },
      {
        title: "Calage du dossier d'aide",
        description: "Identification de l'opération applicable, calcul du volume d'économies selon la surface, l'usage et la baisse de puissance.",
      },
      {
        title: 'Engagement et déploiement',
        description: "L'aide doit être demandée avant de signer le devis. Dépose des luminaires existants, pose des LED, raccordement et tests.",
      },
      {
        title: 'Réception et dépôt',
        description: "Procès-verbal de réception, mesure des niveaux d'éclairement, conservation des fiches techniques. Dépôt du dossier.",
      },
    ],
    faq: [
      {
        question: "Quelle performance viser ?",
        answer:
          "L'aide fixe une performance minimale variable selon l'usage. Au-delà du seuil, il est pertinent de viser des luminaires plus performants (plus de 130 lm/W courant aujourd'hui) et gradables.",
      },
      {
        question: "Faut-il remplacer tout l'éclairage ?",
        answer:
          "Non. L'aide finance chaque luminaire LED remplaçant un luminaire moins performant. Vous pouvez commencer par un étage, un bâtiment, un usage avant d'étendre. Un plan pluriannuel peut être calé sur le rythme des remplacements naturels.",
      },
      {
        question: "Le relamping seul suffit-il ?",
        answer:
          "Rarement. Le relamping apporte un gain notable mais limité à l'éclairage. Pour des objectifs ambitieux, il doit s'inscrire dans un plan plus large couvrant aussi le chauffage, l'enveloppe et l'eau chaude. Le relamping reste le geste de démarrage le plus rapide.",
      },
    ],
    ctaLabel: 'Estimer la prime CEE pour le relamping LED (BAT-EQ-127)',
    ctaHref: '/tertiaire/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (BAT-EQ-127)', href: '/cee/fiches?ref=BAT-EQ-127' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'calorifugeage',
    iconName: 'shield-check',
    eyebrow: 'Calorifugeage, fiche CEE (BAT-TH-146)',
    title: 'Calorifugeage de points singuliers',
    shortLabel: 'Calorifugeage',
    lead: "Le calorifugeage isole les vannes, brides, échangeurs et pompes de vos réseaux de chaleur ou de froid avec des matelas isolants démontables. Fort impact dans les chaufferies et locaux techniques.",
    badge: 'BAT-TH-146',
    badgeLabel: "Fiche d'opération standardisée CEE secteur BAT-TH",
    keyPoints: [
      {
        iconName: 'shield-check',
        label: 'Points singuliers ciblés',
        description: "Vannes, brides, raccords, échangeurs, pompes : tous les points habituellement non isolés d'un réseau de chaleur ou de froid.",
      },
      {
        iconName: 'flame',
        label: 'Matelas démontables',
        description: "Les matelas isolants sont démontables et réutilisables. Ils ne gênent ni la maintenance ni les inspections.",
      },
      {
        iconName: 'gauge',
        label: 'Plage de température',
        description: "Applicable aux réseaux de température comprise entre 40 °C et 200 °C (chauffage, eau chaude, vapeur), ou aux réseaux de froid.",
      },
      {
        iconName: 'sparkles',
        label: 'Gain rapide',
        description: "Chantier court (quelques jours), coût modéré, gain rapide et durable. L'un des gestes les plus simples de votre plan de réduction.",
      },
    ],
    steps: [
      {
        title: 'Audit des locaux techniques',
        description: "Inventaire des points non isolés dans chaque local technique. Mesure des températures de surface et estimation des pertes.",
      },
      {
        title: 'Choix des matelas',
        description: "Sélection des matelas isolants démontables selon la température, la nature des fluides, les contraintes d'accessibilité.",
      },
      {
        title: "Calage du dossier d'aide",
        description: "Identification de l'opération applicable, calcul du volume d'économies selon la nature et la quantité de points à isoler.",
      },
      {
        title: 'Engagement et pose',
        description: "L'aide doit être demandée avant de signer le devis. Pose des matelas par un technicien qualifié, avec étiquetage et plan de récolement.",
      },
      {
        title: 'Réception et dépôt',
        description: "Procès-verbal de réception, mesure des températures après travaux, conservation des fiches techniques. Dépôt du dossier.",
      },
    ],
    faq: [
      {
        question: "Pourquoi isoler les points singuliers d'un réseau déjà isolé ?",
        answer:
          "Sur un réseau de chaleur, la canalisation est généralement isolée. Les points singuliers (vannes, brides, échangeurs) ne le sont pas car il faut y accéder. Or ces points concentrent une part disproportionnée des pertes. Les matelas démontables résolvent le problème : isolation efficace et maintenance possible.",
      },
      {
        question: "Le calorifugeage est-il compatible avec les inspections ?",
        answer:
          "Oui. Les matelas démontables se retirent en quelques minutes pour permettre les inspections et la maintenance. Une fois l'opération réalisée, le matelas est remis en place sans dégradation.",
      },
      {
        question: "Quel volume d'aide pour un calorifugeage ?",
        answer:
          "Le volume dépend du nombre et du type de points à isoler, de la température du réseau et des heures de fonctionnement annuelles. Sur une chaufferie de taille moyenne, le rapport investissement/aide est souvent très favorable.",
      },
    ],
    ctaLabel: 'Estimer la prime CEE pour le calorifugeage (BAT-TH-146)',
    ctaHref: '/tertiaire/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (BAT-TH-146)', href: '/cee/fiches?ref=BAT-TH-146' },
  },
];

export function getTravauxTertiaireStub(slug: string): ParcoursStub | undefined {
  return TRAVAUX_TERTIAIRE.find((p) => p.slug === slug);
}
